import { AlertTriangle, RotateCw } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui';

/**
 * Top-level render-error safety net.
 *
 * Before this existed, nothing in the tree caught a thrown render error —
 * React's own behaviour is to unmount the whole application on an uncaught
 * error, which reads to a field user as "the app is sometimes just blank."
 * A boundary must be a class component; React has no Hook equivalent for
 * getDerivedStateFromError/componentDidCatch.
 *
 * "Retry" first tries to recover in place (remount the subtree, cheap and
 * fast, works for a transient render error). If the same subtree throws
 * again, it falls back to a full reload, which also clears any bad
 * component-local state a retry alone would preserve.
 */

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  retriedInPlace: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, retriedInPlace: false };

  static getDerivedStateFromError(): State {
    return { hasError: true, retriedInPlace: false };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error, info.componentStack);
  }

  private handleRetry = (): void => {
    if (this.state.retriedInPlace) {
      window.location.reload();
      return;
    }

    this.setState({ hasError: false, retriedInPlace: true });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <div className="rounded-full bg-destructive/10 p-3 text-destructive">
            <AlertTriangle className="size-6" />
          </div>
          <div>
            <p className="font-medium text-foreground">Something went wrong</p>
            <p className="mt-1 text-sm text-muted-foreground">
              This screen ran into a problem. Your data is safe — try again.
            </p>
          </div>
          <Button onClick={this.handleRetry} className="mt-1">
            <RotateCw className="size-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }
}
